/**
 * Per-instance selection / hover / preview state, packed into a
 * `THREE.DataTexture`.
 *
 * # Why DataTexture (Q18.B decision)
 *
 * AutoCAD's `SELECT ALL` / Ctrl+A on a 685k-entity drawing must update
 * every entity's render state in ≤ 10 ms. Cloning geometry into a
 * separate "selection mesh" (Option A) requires ~270 MB of allocation
 * and 5 s of GC. Per-vertex shader attributes (Option B) need a buffer
 * sub-data upload per change. Stencil overlay (Option C) doubles draw
 * calls on every frame.
 *
 * The canonical Three.js BatchedMesh pattern (per discourse #72996) is
 * a **DataTexture** with one byte per instance, sampled in the vertex
 * shader. 685k bytes ≈ 700 KB — a single `texSubImage2D` upload, ~5 ms
 * end-to-end on M-class hardware. This implements that.
 *
 * # State byte values
 *
 * | byte | meaning  |
 * |------|----------|
 * | 0    | normal   |
 * | 1    | selected |
 * | 2    | hovered  |
 * | 3    | preview  |
 *
 * `setSelectedIds` / `setHoveredId` / `setPreviewIds` take an external
 * array and diff against the previous state, only writing changed
 * slots. `texture.needsUpdate = true` is flipped after every batch so
 * R3F's invalidate pulse uploads exactly once per frame.
 *
 * # WebGL1 fallback (edge case 23)
 *
 * RedFormat + UnsignedByteType is core to WebGL2. WebGL1 does not
 * accept single-channel byte textures without `OES_texture_float`-
 * style extensions; the safe portable choice on the WebGL1 path is
 * RGBA8 (4× memory, still negligible — 2.7 MB for 685k). The
 * `compatibilityMode` flag picks the right format at construction
 * time.
 *
 * # PR-C scope
 *
 * One texture per BatchedLayer bucket. Lifetime tied to the bucket —
 * disposed alongside it on scene reset.
 */

import * as THREE from "three";

export type SelectionStateByte = 0 | 1 | 2 | 3;

/** Shared empty slot list — the `setHoveredId(null)` / cleared-hover path. */
const EMPTY_SLOTS: readonly number[] = [];

/**
 * Compatibility mode for the underlying texture format.
 *
 * `webgl2` — RedFormat + UnsignedByteType, 1 byte per instance (~700 KB
 * at 685k instances).
 * `webgl1` — RGBAFormat + UnsignedByteType, 4 bytes per instance (~2.7
 * MB at 685k). Sampled in the shader via `.r` channel only.
 */
export type SelectionStateCompat = "webgl2" | "webgl1";

/**
 * Minimal shape of a material that samples a `SelectionStateTexture` —
 * `THREE.ShaderMaterial.uniformsNeedUpdate`. Declared structurally so this
 * module keeps its single `three` import and does not depend on the material
 * patch module (which depends on this one).
 */
export interface SelectionStateSampler {
  uniformsNeedUpdate: boolean;
}

export interface SelectionStateTextureOptions {
  /** Maximum instance count this texture must support. */
  capacity: number;
  /** Format mode — pick from caller via `gl.getParameter(VERSION)`. */
  compat?: SelectionStateCompat;
}

/**
 * Square-pack capacity into a `texSize × texSize` grid. The shader
 * reads `idx → (idx % texSize, idx / texSize)` to recover a UV.
 * Returned `texSize` is `ceil(sqrt(capacity))` with a floor of 1.
 */
function computeTexSize(capacity: number): number {
  if (capacity <= 0) return 1;
  return Math.max(1, Math.ceil(Math.sqrt(capacity)));
}

export class SelectionStateTexture {
  readonly texture: THREE.DataTexture;
  readonly texSize: number;
  readonly capacity: number;
  readonly compat: SelectionStateCompat;
  /** Bytes per pixel in the underlying texture. */
  private readonly bytesPerPixel: number;
  /**
   * Previous-call cache of selected / hovered / preview slot indices.
   * Used by setSelectedIds / setHoveredId(s) / setPreviewIds so we only
   * write the diff between successive calls.
   *
   * S9b (Codex round-5 P3) — hover is a SET, not a single slot: an MLEADER's
   * block-content satellite can share the parent's bucket, so a hover must be
   * able to light MULTIPLE slots in one bucket (see `setHoveredIds`).
   */
  private prevSelectedSlots: Set<number>;
  private prevHoveredSlots: Set<number>;
  private prevPreviewSlots: Set<number>;
  /**
   * Materials that sample this texture (registered by
   * `patchMaterialWithSelectionState`). See {@link flush} for why they have to
   * be told about a pixel change.
   */
  private readonly samplers = new Set<SelectionStateSampler>();

  constructor(options: SelectionStateTextureOptions) {
    this.capacity = options.capacity;
    this.compat = options.compat ?? "webgl2";
    this.texSize = computeTexSize(options.capacity);
    this.bytesPerPixel = this.compat === "webgl1" ? 4 : 1;
    const totalBytes = this.texSize * this.texSize * this.bytesPerPixel;
    const data = new Uint8Array(totalBytes); // initialised to 0 = "normal"

    if (this.compat === "webgl1") {
      this.texture = new THREE.DataTexture(
        data,
        this.texSize,
        this.texSize,
        THREE.RGBAFormat,
        THREE.UnsignedByteType,
      );
    } else {
      this.texture = new THREE.DataTexture(
        data,
        this.texSize,
        this.texSize,
        THREE.RedFormat,
        THREE.UnsignedByteType,
      );
    }
    // Nearest filtering — we want raw byte values, not interpolation.
    this.texture.minFilter = THREE.NearestFilter;
    this.texture.magFilter = THREE.NearestFilter;
    this.texture.wrapS = THREE.ClampToEdgeWrapping;
    this.texture.wrapT = THREE.ClampToEdgeWrapping;
    this.texture.generateMipmaps = false;
    this.texture.needsUpdate = true;

    this.prevSelectedSlots = new Set();
    this.prevHoveredSlots = new Set();
    this.prevPreviewSlots = new Set();
  }

  /**
   * Write a single state byte for one slot. Caller is responsible for
   * batching `needsUpdate = true` (see `flush`) when many slots change
   * within one frame.
   */
  update(slot: number, state: SelectionStateByte): void {
    if (slot < 0 || slot >= this.capacity) return;
    const data = this.texture.image.data as Uint8Array;
    if (this.compat === "webgl1") {
      const baseIdx = slot * 4;
      data[baseIdx] = state;
      // Alpha channel set so RGBA samplers don't blend transparent.
      data[baseIdx + 3] = 255;
    } else {
      data[slot] = state;
    }
  }

  /** Bulk write — same state for many slots. */
  bulkUpdate(slots: ReadonlyArray<number>, state: SelectionStateByte): void {
    for (const slot of slots) this.update(slot, state);
  }

  /**
   * Diff-based selection update. Slots that were selected but aren't
   * any more are zeroed; slots that are newly selected are set to 1.
   * Slots that were already selected aren't touched. The hovered slot
   * (if any) is preserved as state=2 — selection writes don't clobber
   * hover and vice versa.
   *
   * # PR-V6 large-set fast path
   *
   * For bulk selection (Ctrl+A on 685k entities) the diff approach
   * still does N Set lookups + N writes per call. When `slots.length`
   * is comparable to `capacity` we skip the diff entirely and rewrite
   * the whole texture buffer single-pass — `bulkOverwriteSelected`
   * pre-fills with 0 then iterates `slots` once. Identical end-state,
   * faster for large sets.
   */
  setSelectedIds(slots: ReadonlyArray<number>): void {
    // Bulk fast path — when the new set covers a significant fraction
    // of capacity (or is enormous in absolute terms) the diff Sets get
    // expensive. Single-pass overwrite is O(capacity + slots.length)
    // with no per-slot Set allocation cost.
    const len = slots.length;
    if (len >= 1000 && (len * 2 >= this.capacity || len >= 50000)) {
      this.bulkOverwriteSelected(slots);
      return;
    }

    const next = new Set(slots);
    // Clear slots that left the selection.
    for (const prev of this.prevSelectedSlots) {
      if (!next.has(prev)) {
        // Don't clobber a live hover (now a multi-slot set).
        if (!this.prevHoveredSlots.has(prev)) {
          this.update(prev, 0);
        }
      }
    }
    // Mark newly selected slots.
    for (const slot of next) {
      if (!this.prevSelectedSlots.has(slot)) {
        this.update(slot, 1);
      }
    }
    this.prevSelectedSlots = next;
    this.flush();
  }

  /**
   * PR-V6 — bulk overwrite path. Used by `setSelectedIds` for large
   * sets (>= 1000 slots) and externally for benchmarks. Single-pass:
   *
   *   1. Zero the underlying byte buffer (or quad-byte buffer on WebGL1)
   *   2. Iterate `slots` once, writing 1 (selected) at each index
   *   3. Re-stamp the live hover so it survives the wholesale rewrite
   *
   * Identical end-state to the diff path but skips the Set
   * allocations / per-slot Set lookups, which become hot at 100k+
   * slot counts.
   */
  bulkOverwriteSelected(slots: ReadonlyArray<number>): void {
    const data = this.texture.image.data as Uint8Array;
    // Single-pass zero. `Uint8Array.fill(0)` is one tight memset call
    // — cheaper than per-slot writes even when only a few slots
    // changed at this size.
    data.fill(0);
    if (this.compat === "webgl1") {
      // Re-write the alpha=255 watermark for every pixel.
      for (let i = 3; i < data.length; i += 4) data[i] = 255;
    }
    const next = new Set<number>();
    if (this.compat === "webgl1") {
      for (const slot of slots) {
        if (slot < 0 || slot >= this.capacity) continue;
        const baseIdx = slot * 4;
        data[baseIdx] = 1;
        next.add(slot);
      }
    } else {
      for (const slot of slots) {
        if (slot < 0 || slot >= this.capacity) continue;
        data[slot] = 1;
        next.add(slot);
      }
    }
    // Preserve live hover — bulk-zero clobbered it, restore byte 2 for every
    // hovered slot (multi-slot set; S9b round-5).
    for (const hovered of this.prevHoveredSlots) {
      this.update(hovered, 2);
    }
    // Re-stamp live preview slots — they live at byte 3.
    for (const previewSlot of this.prevPreviewSlots) {
      this.update(previewSlot, 3);
    }
    this.prevSelectedSlots = next;
    this.flush();
  }

  /**
   * Diff-based hover update. A null slot clears the hover. Thin wrapper over
   * {@link setHoveredIds} — kept for the single-hover callers (per-frame hover
   * bridge's non-fan-out path, tests).
   */
  setHoveredId(slot: number | null): void {
    this.setHoveredIds(slot === null ? EMPTY_SLOTS : [slot]);
  }

  /**
   * S9b (Codex round-5 P3 — "Highlight same-bucket MLEADER satellites on
   * hover"): diff-based MULTI-slot hover update. An MLEADER whose block-content
   * satellite shares the parent's bucket needs BOTH the parent slot AND the
   * satellite slot lit in that one bucket; the previous single-slot
   * `prevHoveredSlot` could only track one, so a same-bucket satellite was
   * dropped and only the leader segment highlighted. Tracks a SET of hovered
   * slots (mirrors `setSelectedIds` / `setPreviewIds`). Slots leaving the set
   * revert to selected (byte 1) if still selected, else normal (byte 0) —
   * identical to the old single-slot revert. Newly-hovered slots are stamped
   * byte 2 (hover wins over selected, matching the prior behaviour).
   */
  setHoveredIds(slots: ReadonlyArray<number>): void {
    const next = new Set(slots);
    // Revert slots that left the hover set.
    for (const prev of this.prevHoveredSlots) {
      if (!next.has(prev)) {
        const reverted: SelectionStateByte = this.prevSelectedSlots.has(prev)
          ? 1
          : 0;
        this.update(prev, reverted);
      }
    }
    // Stamp newly-hovered slots.
    for (const slot of next) {
      if (!this.prevHoveredSlots.has(slot)) {
        this.update(slot, 2);
      }
    }
    this.prevHoveredSlots = next;
    this.flush();
  }

  /**
   * Diff-based draft-preview update. Behaves like `setSelectedIds`
   * but writes byte 3 instead of 1.
   */
  setPreviewIds(slots: ReadonlyArray<number>): void {
    const next = new Set(slots);
    for (const prev of this.prevPreviewSlots) {
      if (!next.has(prev)) {
        const fallback: SelectionStateByte = this.prevSelectedSlots.has(prev)
          ? 1
          : this.prevHoveredSlots.has(prev)
            ? 2
            : 0;
        this.update(prev, fallback);
      }
    }
    for (const slot of next) {
      if (!this.prevPreviewSlots.has(slot)) {
        this.update(slot, 3);
      }
    }
    this.prevPreviewSlots = next;
    this.flush();
  }

  /**
   * Register a material that samples this texture, so {@link flush} can force
   * its uniform list to be re-uploaded. Called by
   * `patchMaterialWithSelectionState`; idempotent (a `Set`).
   */
  registerSampler(material: SelectionStateSampler): void {
    this.samplers.add(material);
  }

  /**
   * Flush pending writes.
   *
   * `texture.needsUpdate = true` marks the pixel data dirty, but that ALONE is
   * not enough to get it onto the GPU. three uploads a `DataTexture`'s changed
   * pixels inside `WebGLTextures.setTexture2D`, and that runs only while a
   * material's uniform list is being refreshed — which `WebGLRenderer.setProgram`
   * skips when neither the program nor the material changed since the previous
   * draw. A scene whose only drawn object is one bucket mesh hits exactly that
   * case: frame after frame the same material is bound, the uniform list is
   * never re-uploaded, and the new state bytes never reach the sampler. The
   * geometry of the very same pass paints correctly (it rides vertex buffers,
   * not the uniform path), so the entity shows up in its LAYER colour while the
   * "selected" byte sits unread in CPU memory — the exact shape of
   * `MBCR-selection-highlight-intermittent`. It looked intermittent because ANY
   * unrelated later draw (a second bucket, a dialog-driven material swap) flips
   * `_currentMaterialId` and incidentally flushes the upload.
   *
   * `ShaderMaterial.uniformsNeedUpdate` is three's documented opt-in for
   * "my uniform VALUES changed" and forces that refresh on the next render. The
   * bucket materials are `LineMaterial`, a `ShaderMaterial` subclass, so the
   * flag is honoured. Setting it on a non-ShaderMaterial sampler is inert, not
   * harmful.
   */
  flush(): void {
    this.texture.needsUpdate = true;
    for (const sampler of this.samplers) {
      sampler.uniformsNeedUpdate = true;
    }
  }

  /**
   * Reset all slots to 0 ("normal"). Used by `setEntities` when the
   * scene is wholesale-replaced.
   */
  clear(): void {
    const data = this.texture.image.data as Uint8Array;
    data.fill(0);
    if (this.compat === "webgl1") {
      // Re-write the alpha=255 watermark for every pixel.
      for (let i = 3; i < data.length; i += 4) data[i] = 255;
    }
    this.prevSelectedSlots.clear();
    this.prevHoveredSlots.clear();
    this.prevPreviewSlots.clear();
    this.flush();
  }

  dispose(): void {
    this.samplers.clear();
    this.texture.dispose();
  }
}

/**
 * Detect the current GL context's WebGL major version. Returns
 * `"webgl1"` if the renderer is WebGL1 only, `"webgl2"` if WebGL2 or
 * WebGPU. Used by callers to pick the right `compat` mode.
 */
export function detectSelectionStateCompat(
  renderer: THREE.WebGLRenderer | { capabilities?: { isWebGL2?: boolean } },
): SelectionStateCompat {
  const caps = (renderer as { capabilities?: { isWebGL2?: boolean } })
    .capabilities;
  if (caps && caps.isWebGL2 === false) return "webgl1";
  return "webgl2";
}
